import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, Flame, Medal } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

interface Entry {
  display_name: string;
  current_streak: number;
  longest_streak: number;
  lessons_completed: number;
}

const Leaderboard = () => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await (supabase as any).rpc("get_leaderboard", { limit_count: 5 });
      setEntries((data as Entry[]) || []);
      setLoading(false);
    };
    load();
  }, []);

  const rankColor = (i: number) =>
    i === 0 ? "text-amber-500" : i === 1 ? "text-slate-400" : i === 2 ? "text-amber-700" : "text-muted-foreground";

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" /> Top Learners
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No learners yet — be the first!</p>
        ) : (
          <ol className="space-y-1.5">
            {entries.map((e, i) => (
              <li
                key={`${e.display_name}-${i}`}
                className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`font-bold w-5 text-sm ${rankColor(i)}`}>
                    {i < 3 ? <Medal className="w-4 h-4" /> : i + 1}
                  </span>
                  <span className="font-medium text-sm truncate">{e.display_name}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                  <span className="flex items-center gap-1">
                    <Flame className="w-3 h-3 text-orange-500" /> {e.current_streak}
                  </span>
                  <span className="font-semibold text-foreground">{e.lessons_completed} ✓</span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};

export default Leaderboard;
